import React, { useCallback, useEffect, useState } from 'react';
import { Modal, TextField } from '@ofself/shell';
import { DeskTable, ElementTable, ElementCell } from '../../components/nucleus/Shelf';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import { Cell } from '../../components/nucleus/Collection';
import { Empty } from '../../components/nucleus/Empty';
import { useOpen, Facts } from '../../components/nucleus/Inspector';
import { useToast } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { usePlace } from '../../nucleus/place';
import api from '../../services/api';
import { EVENTS } from '../../shared';
import { FormColumn, FormBand, ZoneField, zonePlace } from './selfForm';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import IdentityInspector, { identityActions, IdentityFace } from './IdentityInspector';
import { useIdentityScope, nameOf, photoUrlOf, IDENTITY_FIELDS } from './selfData';
import { Plus } from 'lucide-react';
import { dateOf, plural } from '../../nucleus/format';

// PROFILE — who you are here: the faces you present (identities, a collection,
// each opening in the inspector) and, for a person, the username everyone
// finds you by. In a group the identities are the group's own.
export default function ProfilePage() {
    const { section, page, groupId } = usePlace();
    const scope = useIdentityScope();
    const toast = useToast();
    const [openId, open, close] = useOpen();
    const [list, setList] = useState(null);
    const [usage, setUsage] = useState({ follows_default: 0, by_identity: {} });
    const [photos, setPhotos] = useState({});
    const [making, setMaking] = useState(false);
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        const [res, use] = await Promise.all([
            scope.list().catch(() => null),
            scope.usage().catch(() => null),
        ]);
        if (!res) { setFailed(true); return; }
        setFailed(false);
        const all = res?.nodes || (Array.isArray(res) ? res : []);
        setList(all);
        setUsage(use || { follows_default: 0, by_identity: {} });
        const pairs = await Promise.all(all.map(async (n) => [n.id, await photoUrlOf(n.value_json?.avatar_file_id)]));
        setPhotos(Object.fromEntries(pairs));
    }, [scope]);
    useEffect(() => { setList(null); load(); }, [scope.key]); // eslint-disable-line react-hooks/exhaustive-deps

    const changed = () => { window.dispatchEvent(new CustomEvent(EVENTS.PROFILE_AVATAR_CHANGED)); return load(); };
    const current = list?.find((n) => String(n.id) === String(openId)) || null;
    const ordered = (list || []).slice().sort((a, b) => (b.id === scope.defaultId) - (a.id === scope.defaultId));

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={list ? list.length : undefined}
            verbs={(
                <Verb
                    primary
                    icon={<Plus />}
                    disabled={!scope.canEdit}
                    title={scope.canEdit ? undefined : scope.denied}
                    onClick={() => setMaking(true)}
                >
                    New identity
                </Verb>
            )}
            inspector={current && (
                <IdentityInspector
                    key={current.id}
                    node={current}
                    photo={photos[current.id]}
                    scope={scope}
                    usage={usage}
                    identities={list}
                    onClose={close}
                    onChanged={changed}
                    onDeleted={() => { close(); changed(); }}
                />
            )}
        >
            {failed && <Empty retry={load}>Couldn’t read the identities.</Empty>}
            <DeskTable className="self-desk" top={0} bottom={8} between={40}>
                <ElementTable heads={{ tag: '' }}
                    title={groupId ? `${scope.fallbackName || 'The group'}’s identities` : 'Your identities'}
                    count={list ? list.length : undefined}
                    empty={list ? (scope.canEdit ? 'No identity yet. New identity makes the first.' : 'This group has no identity yet.') : undefined}
                >
                    {ordered.map((n) => {
                        const isDefault = n.id === scope.defaultId;
                        const rides = usage.by_identity?.[String(n.id)] || {};
                        return (
                            <Cell key={n.id} actions={identityActions({ node: n, scope, usage, identities: list, open, onChanged: changed, onDeleted: changed, toast })}>
                                <ElementCell
                                    tag={isDefault ? 'DEFAULT' : undefined}
                                    mark={<IdentityFace name={nameOf(n)} url={photos[n.id]} size={50} />}
                                    name={nameOf(n)}
                                    note={rides.relationships ? plural(rides.relationships, 'relationship')
                                        : rides.realms ? `named by ${plural(rides.realms, 'realm')}` : (isDefault ? 'the default' : 'not named yet')}
                                    current={String(n.id) === String(openId)}
                                    onPress={() => open(n.id)}
                                    title={nameOf(n)}
                                />
                            </Cell>
                        );
                    })}
                </ElementTable>
            </DeskTable>
            {!groupId && <FormColumn><UsernameBand /><AccountBand /></FormColumn>}
            <NewIdentity
                open={making}
                scope={scope}
                first={!list?.length}
                onClose={() => setMaking(false)}
                onMade={async (made) => { setMaking(false); await changed(); open(made.id); }}
            />
        </NucleusPage>
    );
}

// YOUR ACCOUNT — facts of the person, not of any face: the email ofSelf reaches
// you at (no change of email yet), when you joined, and the one timezone, set
// here and read by everything else (quiet hours, dates).
function AccountBand() {
    const { user, refreshUser } = useAuth();
    const { applyPrefs } = useNotifications();
    const toast = useToast();
    const zone = user?.timezone || '';
    const saveZone = async (next) => {
        if (!next || next === zone) return;
        try {
            await api.updateUser(user.id, { timezone: next });
            refreshUser?.();
            // the inbox reads quiet hours in it on this device too
            api.getNotificationSettings().then((s) => applyPrefs?.(s)).catch(() => {});
            toast.success(`Timezone set to ${zonePlace(next).city}`);
        } catch {
            toast.error('Could not change your timezone');
        }
    };
    return (
        <FormBand title="Account" lead="How ofSelf reaches you. Changing your email is not available yet.">
            <Facts items={[
                { label: 'Email', value: user?.email || '—' },
                { label: 'Member since', value: dateOf(user?.created_at) || '—' },
            ]} />
            <ZoneField value={zone} onChange={saveZone} />
            <p className="nx-note">Quiet hours and dates are read in it, everywhere you are signed in.</p>
        </FormBand>
    );
}

// CREATING ONE is a Modal (the grammar's place to create): its name, and for a
// person their first and last name. The first identity becomes the default.
function NewIdentity({ open, scope, first, onClose, onMade }) {
    const toast = useToast();
    const [name, setName] = useState('');
    const [fields, setFields] = useState({});
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) { setName(''); setFields({}); } }, [open]);
    const shown = IDENTITY_FIELDS.filter((f) => scope.kind === 'personal' || !f.personalOnly);

    const make = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) return;
        setBusy(true);
        try {
            const res = await scope.create({ title: trimmed, value_json: { ...fields, display_name: trimmed } });
            const made = res?.node || res;
            if (first || !scope.defaultId) {
                await scope.setDefault(made.id);
                await scope.refresh?.();
            }
            toast.success(scope.kind === 'group' ? `${trimmed} is one of ${scope.fallbackName || 'the group'}’s identities now` : `${trimmed} is one of your faces now`);
            onMade(made);
        } catch (err) {
            toast.error(err?.message || 'Could not make that identity');
        } finally { setBusy(false); }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="New identity"
            lead={scope.kind === 'group' ? 'A face this group presents. A realm can name it.' : 'A face you present. A realm can name it, so an app sees this one instead of your default.'}
            size="md"
        >
            <form className="self-modal-form" onSubmit={make}>
                <TextField id="self-new-name" label="Name" value={name} autoFocus required onChange={(e) => setName(e.target.value)}
                    placeholder={scope.kind === 'group' ? 'How this group is known' : 'Work, Public, Family…'} />
                {shown.map((f) => (
                    <TextField key={f.key} id={`self-new-${f.key}`} label={f.label} value={fields[f.key] || ''} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })} />
                ))}
                <SheetFoot>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    <FootButton primary type="submit" disabled={!name.trim()} busy={busy}>Make it</FootButton>
                </SheetFoot>
            </form>
        </Modal>
    );
}

// YOUR USERNAME — not an identity: unique, how anyone finds you, and every app
// you connect receives it whatever face you present. 30 days between changes.
const USERNAME_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // mirrors the backend

function UsernameBand() {
    const { user, refreshUser } = useAuth();
    const toast = useToast();
    const [username, setUsername] = useState(user?.username || '');
    useEffect(() => { setUsername(user?.username || ''); }, [user?.username]);
    const until = user?.username_changed_at ? new Date(new Date(user.username_changed_at).getTime() + USERNAME_COOLDOWN_MS) : null;
    const cooling = until ? Date.now() < until.getTime() : false;
    const next = username.trim();
    const changed = next !== '' && next !== (user?.username || '');

    return (
        <FormBand
            title="Username"
            lead="How people find you. Every app you connect receives it, whichever identity you present."
            acts={[{
                id: 'save', label: 'Save', primary: true,
                disabled: !changed || cooling,
                hint: cooling ? `You can change it again on ${dateOf(until)}` : undefined,
                confirm: { title: `Change your username to ${next}?`, body: `People find you as ${next} from now on, and you can’t change it again for 30 days.`, label: 'Change it' },
                run: async () => {
                    try {
                        await api.updateUser(user.id, { username: next });
                        await refreshUser?.();
                        toast.success(`You are ${next} now`);
                    } catch (e) {
                        setUsername(user?.username || '');
                        throw e;
                    }
                },
            }]}
        >
            <TextField
                aria-label="Username"
                value={username}
                disabled={cooling}
                onChange={(e) => setUsername(e.target.value)}
                hint={cooling ? `Changed recently. You can change it again on ${dateOf(until)}.` : 'Once changed, it stays for 30 days.'}
            />
        </FormBand>
    );
}
